import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';

type IncidentBranch = {
  id?: UUID;
  incident_id: UUID;
  name?: string;
  branch_director_name?: string;
  branch_deputy?: string;
  division_list?: string[];
};

export const keys = {
  base: 'branches',
  get all() {
    return [keys.base];
  },
  get: function (group_guid: UUID, user_guid: UUID) {
    return [keys.base, group_guid, user_guid];
  },
} as const;

export const useIncidentBranches = (incidentId: string) => {
  return useQuery({
    queryKey: ['branches', incidentId],
    queryFn: async () => {
      const resp = await axios.get<IncidentBranch[]>(
        `${config.apiGateway.ics_incident_branches}/${incidentId}`
      );
      return resp.data;
    },
    enabled: !!incidentId,
    throwOnError: () => {
      return false;
    },
  });
};

export const useCreateIncidentBranch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newIncidentBranch: IncidentBranch) => {
      const resp = await axios.post<IncidentBranch>(
        `${config.apiGateway.ics_incident_branches}`,
        newIncidentBranch
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useUpdateIncidentBranch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updatedIncidentBranch: IncidentBranch) => {
      const resp = await axios.put<IncidentBranch>(
        `${config.apiGateway.ics_incident_branches}/${updatedIncidentBranch.id}`,
        updatedIncidentBranch
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useDeleteIncidentBranch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID) => {
      await axios.delete(`${config.apiGateway.ics_incident_branches}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};
